/* The webpackChunkName values defined in the imports instructs Webpack how to bundle different files together. Essentially, we create different bundles based on the privileges/roles
   of the user. This means that a public user, for instance, may never need to load any of the bundles designed for site admins, station heads, and station collectors. However,
   the resulting app ultimately includes references to all the scripts, but with a deferred load. So a public user may end up loading all the scripts (which means if they were to
   log in, everything they needed would be preloaded), but the scripts most relevant to them are loaded first.
*/


import Vue from 'vue'
import VueRouter from 'vue-router'
import BootstrapVue from 'bootstrap-vue'
import App from './App.vue'

import './custom.scss'

// Font Awesome
import { library } from '@fortawesome/fontawesome-svg-core'
import { faSquare, faCheckSquare, faTable, faEdit, faSortAmountUp, faSortAmountDown, faTrash, faPlus, faArrowUp, faArrowDown, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
//import { fas } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

// Mixins
import CrudOperationsMixin from './components/Common/CrudOperationsMixin.js'
import AllergenCollectionMixins from './components/Common/AllergenCollectionMixins.js'
import DateTimeMixins from './components/Common/DateTimeMixins.js'
import SubscriptionMixins from './components/Common/SubscriptionMixins.js'

// Form Controls
const TextControl = () => import(/* webpackChunkName: "controls" */ './components/Controls/TextControl.vue')
const TextareaControl = () => import(/* webpackChunkName: "controls" */ './components/Controls/TextareaControl.vue')
const SelectControl = () => import(/* webpackChunkName: "controls" */ './components/Controls/SelectControl.vue')
const CheckboxControl = () => import(/* webpackChunkName: "controls" */ './components/Controls/CheckboxControl.vue')

// Widgets
import PageLoading from './components/Common/PageLoading.vue'
import PageTitle from './components/Common/PageTitle.vue'
import stationPageTitle from './components/Common/stationPageTitle.vue'
import AuthDropdown from './components/AuthDropdown.vue'
import AllergenCollectionSetCard from './components/Common/AllergenCollectionSetCard.vue'
import GaugeWidget from './components/Common/GaugeWidget.vue'
import ContentBlock from './components/Common/ContentBlock.vue'
import AuthForm from './components/Common/AuthForm.vue'
import AuthModal from './components/Common/AuthModal.vue'
import MapWidget from './components/Home/MapWidget.vue'
const FilteredTable = () => import(/* webpackChunkName: "controls" */ './components/Common/FilteredTable.vue')

// Pages
const Testing = () => import(/* webpackChunkName: "script4" */ './components/Testing.vue')
import Login from './components/Login.vue'
import Logout from './components/Logout.vue'
import Home from './components/Home.vue'
import NotFound from './components/NotFound.vue'
import ResetPassword from './components/ResetPassword.vue'

// Public Pages
import StationView from './components/Station/View.vue'
import StationCalendar from './components/Station/Calendar.vue'
import StationDetails from './components/Station/Details.vue'

import ContentBlockView from './components/ContentBlock/View.vue'

// Site Admin Pages script1 - site-admin, script2 - station-admin, script3 - station-manager, script4 - Testing
const AllergenList = () => import(/* webpackChunkName: "script1" */ './components/Allergen/List.vue')
const AllergenFields = () => import(/* webpackChunkName: "script1" */ './components/Allergen/Fields.vue')
const AllergenAdd = () => import(/* webpackChunkName: "script1" */ './components/Allergen/Add.vue')
const AllergenEdit = () => import(/* webpackChunkName: "script1" */ './components/Allergen/Edit.vue')

const ApplicationUserList = () => import(/* webpackChunkName: "script1" */ './components/ApplicationUser/List.vue')
const ApplicationUserFields = () => import(/* webpackChunkName: "script1" */ './components/ApplicationUser/Fields.vue')
const ApplicationUserEdit = () => import(/* webpackChunkName: "script1" */ './components/ApplicationUser/Edit.vue')
const ApplicationUserRoleList = () => import(/* webpackChunkName: "script1" */ './components/ApplicationUser/ApplicationUserRoleList.vue')
const ApplicationUserSubscriptionList = () => import(/* webpackChunkName: "script1" */ './components/ApplicationUser/ApplicationUserSubscriptionList.vue')

const ContentBlockList = () => import(/* webpackChunkName: "script1" */ './components/ContentBlock/List.vue')
const ContentBlockFields = () => import(/* webpackChunkName: "script1" */ './components/ContentBlock/Fields.vue')
const ContentBlockAdd = () => import(/* webpackChunkName: "script1" */ './components/ContentBlock/Add.vue')
const ContentBlockEdit = () => import(/* webpackChunkName: "script1" */ './components/ContentBlock/Edit.vue')

const ReportAdmin = () => import(/* webpackChunkName: "script1" */ './components/Report/Admin.vue')

// Station Admin Pages
const StationList = () => import(/* webpackChunkName: "script2" */ './components/Station/List.vue')
const StationFields = () => import(/* webpackChunkName: "script2" */ './components/Station/Fields.vue')
const StationAdd = () => import(/* webpackChunkName: "script2" */ './components/Station/Add.vue')
const StationEdit = () => import(/* webpackChunkName: "script2" */ './components/Station/Edit.vue')
const StationRoleList = () => import(/* webpackChunkName: "script2" */ './components/Station/StationRoleList.vue')
const StationSubscriptionList = () => import(/* webpackChunkName: "script2" */ './components/Station/StationSubscriptionList.vue')
const StationAllergens = () => import(/* webpackChunkName: "script2" */ './components/Station/StationAllergens.vue')
const StationScales = () => import(/* webpackChunkName: "script2" */ './components/Station/StationScales.vue')

const ScaleSetEdit = () => import(/* webpackChunkName: "script2" */ './components/ScaleSet/Edit.vue')

const RoleFields = () => import(/* webpackChunkName: "script2" */ './components/Role/Fields.vue')
const RoleEdit = () => import(/* webpackChunkName: "script2" */ './components/Role/Edit.vue')
const RoleAdd = () => import(/* webpackChunkName: "script2" */ './components/Role/Add.vue')

const SubscriptionFields = () => import(/* webpackChunkName: "script2" */ './components/Subscription/Fields.vue')
const SubscriptionEdit = () => import(/* webpackChunkName: "script2" */ './components/Subscription/Edit.vue')
const SubscriptionAdd = () => import(/* webpackChunkName: "script2" */ './components/Subscription/Add.vue')

const ReportAdHoc = () => import(/* webpackChunkName: "script2" */ './components/Report/AdHoc.vue')

// Station Manager
const AllergenCollectionSetList = () => import(/* webpackChunkName: "script3" */ './components/AllergenCollectionSet/List.vue')
const AllergenCollectionSetFields = () => import(/* webpackChunkName: "script3" */ './components/AllergenCollectionSet/Fields.vue')
const AllergenCollectionSetAdd = () => import(/* webpackChunkName: "script3" */ './components/AllergenCollectionSet/Add.vue')
const AllergenCollectionSetEdit = () => import(/* webpackChunkName: "script3" */ './components/AllergenCollectionSet/Edit.vue')
const AllergenCollectionSetImport = () => import(/* webpackChunkName: "script3" */ './components/AllergenCollectionSet/Import.vue')

Vue.use(VueRouter);
Vue.use(BootstrapVue);

library.add(faSquare, faCheckSquare, faTable, faEdit, faSortAmountUp, faSortAmountDown, faTrash, faPlus, faArrowUp, faArrowDown, faQuestionCircle);
//library.add(fas);

Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('page-loading', PageLoading);
Vue.component('page-title', PageTitle);
Vue.component('stationPage-title', stationPageTitle);
Vue.component('filtered-table', FilteredTable);
Vue.component('auth-dropdown', AuthDropdown);
Vue.component('allergen-collection-set-card', AllergenCollectionSetCard)
Vue.component('gauge-widget', GaugeWidget);
Vue.component('content-block', ContentBlock);
Vue.component('auth-form', AuthForm);
Vue.component('auth-modal', AuthModal);
Vue.component('map-widget', MapWidget);

Vue.component('text-control', TextControl);
Vue.component('textarea-control', TextareaControl);
Vue.component('select-control', SelectControl);
Vue.component('checkbox-control', CheckboxControl);

Vue.component('application-user-fields', ApplicationUserFields);
Vue.component('application-user-role-list', ApplicationUserRoleList);
Vue.component('application-user-subscription-list', ApplicationUserSubscriptionList);
Vue.component('station-fields', StationFields);
Vue.component('station-subscription-list', StationSubscriptionList);
Vue.component('station-allergens', StationAllergens);
Vue.component('station-scales', StationScales);
Vue.component('station-role-list', StationRoleList);
Vue.component('role-fields', RoleFields);
Vue.component('subscription-fields', SubscriptionFields);
Vue.component('allergen-fields', AllergenFields);
Vue.component('allergen-collection-set-fields', AllergenCollectionSetFields);
Vue.component('content-block-fields', ContentBlockFields);

Vue.config.productionTip = false;

const routes = [
  { path: '/', component: Home, meta: { public: true } },
  { path: '/testing', component: Testing, meta: { public: true } },
  { path: '/login', component: Login, meta: { public: true } },
  { path: '/logout', component: Logout, meta: { public: true } },
  { path: '/resetPassword', component: ResetPassword, meta: { public: true } },
  { path: '/station', component: StationList },
  { path: '/station/add', component: StationAdd },
  { path: '/station/:id', component: StationView, meta: { public: true } },
  { path: '/station/:id/allergenCollectionSet/:allergenCollectionSetId', component: StationView, meta: { public: true } },
  { path: '/station/:id/calendar', component: StationCalendar, meta: { public: true } },
  { path: '/station/:id/calendar/:year/:month', component: StationCalendar, meta: { public: true } },
  { path: '/station/:id/details', component: StationDetails, props: true, meta: { public: true } },
  { path: '/station/:id/edit', component: StationEdit, props: true },
  { path: '/scaleSet/master', component: ScaleSetEdit },
  { path: '/role/add', component: RoleAdd, props: (route) => ({ stationId: route.query.stationId, applicationUserId: route.query.applicationUserId }) },
  { path: '/role/:id', component: RoleEdit, props: true },
  { path: '/subscription/add', component: SubscriptionAdd, props: (route) => ({ stationId: route.query.stationId, applicationUserId: route.query.applicationUserId }) },
  { path: '/subscription/:id', component: SubscriptionEdit, props: true },
  { path: '/allergen', component: AllergenList },
  { path: '/allergen/add', component: AllergenAdd },
  { path: '/allergen/:id', component: AllergenEdit },
  { path: '/applicationUser', component: ApplicationUserList },
  { path: '/applicationUser/:id', component: ApplicationUserEdit, props: true },
  { path: '/allergenCollectionSet', component: AllergenCollectionSetList },
  { path: '/allergenCollectionSet/add', component: AllergenCollectionSetAdd },
  { path: '/allergenCollectionSet/import', component: AllergenCollectionSetImport },
  { path: '/allergenCollectionSet/:id', component: AllergenCollectionSetEdit, props: true },
  { path: '/contentBlock', component: ContentBlockList },
  { path: '/contentBlock/add', component: ContentBlockAdd },
  { path: '/contentBlock/:id', component: ContentBlockView, props: true },
  { path: '/contentBlock/:id/edit', component: ContentBlockEdit, props: true },
  { path: '/pages/:name', component: ContentBlockView, props: true, meta: { public: true } },
  { path: '/report/adHoc', component: ReportAdHoc, props: true },
  { path: '/report/admin', component: ReportAdmin, props: true },
  { path: '*', component: NotFound, meta: { public: true } }
];

const router = new VueRouter({
  routes: routes
});

router.beforeEach((to, from, next) => {
  if (router.app.$root.$data && router.app.$root.$data.authenticatedUser && router.app.$root.$data.authenticatedUser.id) {
    // Allow authenticated users through
    next();
  } else if (to.matched.some(record => record.meta.public)) {
    // Allow all users to hit any page marked as public
    next();
  } else {
    // Redirect to the login page, passing the requested page as the redirect param
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    });
  }
});

new Vue({
  router,
  render: h => h(App),
  mixins: [CrudOperationsMixin, AllergenCollectionMixins, DateTimeMixins, SubscriptionMixins],
  data: {
    authenticatedUser: {
      username: null,
      id: null,
      roles: []
    }
  }
}).$mount('#app');
